/** Route-derived title, breadcrumbs, subtitle, and project context. */
import {
	Box,
	Breadcrumbs,
	Link as MuiLink,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link as RouterLink } from "react-router-dom";

import { reducedMotionMediaQuery } from "../../theme/motionTokens";
import { usePageHeaderState } from "../PageHeaderContext";

export function PageContextBar() {
	const theme = useTheme();
	const { title, breadcrumbs, subtitle } = usePageHeaderState();
	const items = breadcrumbs ?? [];

	return (
		<Box
			component="header"
			sx={{
				px: { xs: 2, sm: 3, lg: 4 },
				py: { xs: 1.5, sm: 1.75 },
				color: "workspace.textPrimary",
				backgroundColor: "rgba(18, 24, 21, 0.64)",
				borderBottom: `1px solid ${theme.palette.workspace.border}`,
				backdropFilter: "blur(10px)",
				WebkitBackdropFilter: "blur(10px)",
			}}
		>
			<Stack
				direction={{ xs: "column", md: "row" }}
				alignItems={{ xs: "flex-start", md: "flex-end" }}
				justifyContent="space-between"
				spacing={1}
			>
				<Box sx={{ minWidth: 0, maxWidth: 960 }}>
					{items.length > 0 && (
						<Breadcrumbs
							separator={<NavigateNextIcon sx={{ fontSize: 15 }} />}
							aria-label="Breadcrumb"
							maxItems={4}
							sx={{
								mb: 0.5,
								color: "workspace.textMuted",
								"& .MuiBreadcrumbs-ol": { flexWrap: "wrap" },
							}}
						>
							{items.map((crumb, index) => {
								const isLast = index === items.length - 1;
								if (isLast || !crumb.to) {
									return (
										<Typography
											key={`${crumb.label}-${index}`}
											component="span"
											sx={{
												fontSize: 12,
												fontWeight: isLast ? 650 : 450,
												color: isLast
													? "workspace.textSecondary"
													: "workspace.textMuted",
											}}
										>
											{crumb.label}
										</Typography>
									);
								}
								return (
									<MuiLink
										key={`${crumb.label}-${index}`}
										component={RouterLink}
										to={crumb.to}
										underline="hover"
										sx={{
											fontSize: 12,
											color: "workspace.textMuted",
											transition: theme.motion.transition.focus,
											"&:hover": { color: "workspace.blue" },
											[reducedMotionMediaQuery]: { transition: "none" },
										}}
									>
										{crumb.label}
									</MuiLink>
								);
							})}
						</Breadcrumbs>
					)}

					<Typography
						variant="h5"
						component="h1"
						sx={{
							fontSize: { xs: "1.15rem", sm: "1.35rem" },
							fontWeight: 700,
							color: "workspace.textPrimary",
							lineHeight: 1.25,
						}}
					>
						{title}
					</Typography>
					{subtitle && (
						<Typography
							variant="body2"
							sx={{
								mt: 0.35,
								color: "workspace.textSecondary",
								lineHeight: 1.5,
							}}
						>
							{subtitle}
						</Typography>
					)}
				</Box>
			</Stack>
		</Box>
	);
}
